
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as inner from '../inner'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * ManagerOrderCreateParams struct
 */
class ManagerOrderCreateParams {
  /**
   * Initialize struct
   * @param {!string=} managerComment
   * @param {number=} executionPrice
   * @constructor
   */
  constructor (managerComment = '', executionPrice = null) {
    this.ManagerComment = managerComment
    this.ExecutionPrice = executionPrice
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ManagerOrderCreateParams}
   * @param {!ManagerOrderCreateParams} other Other struct
   * @returns {!ManagerOrderCreateParams} This struct
   */
  copy (other) {
    if (other.ManagerComment != null) {
      this.ManagerComment = other.ManagerComment
    } else {
      this.ManagerComment = null
    }
    if (other.ExecutionPrice != null) {
      this.ExecutionPrice = other.ExecutionPrice
    } else {
      this.ExecutionPrice = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ManagerOrderCreateParams}
   * @returns {!ManagerOrderCreateParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ManagerOrderCreateParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ManagerOrderCreateParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ManagerOrderCreateParams}
   * @param {!ManagerOrderCreateParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ManagerOrderCreateParams)) {
      throw new TypeError('Instance of ManagerOrderCreateParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ManagerOrderCreateParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      ManagerComment: ((this.ManagerComment != null) ? this.ManagerComment : null),
      ExecutionPrice: ((this.ExecutionPrice != null) ? this.ExecutionPrice : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ManagerOrderCreateParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ManagerOrderCreateParams} other Object value
   * @returns {!ManagerOrderCreateParams} Created struct
   */
  static fromObject (other) {
    return new ManagerOrderCreateParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ManagerOrderCreateParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ManagerOrderCreateParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ManagerOrderCreateParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 12044
  }
}

export { ManagerOrderCreateParams };

/**
 * Fast Binary Encoding ManagerOrderCreateParams field model
 */
class FieldModelManagerOrderCreateParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.ManagerComment = new fbe.FieldModelString(buffer, 4 + 4)
    this.ExecutionPrice = new fbe.FieldModelOptional(new fbe.FieldModelDouble(buffer, this.ManagerComment.fbeOffset + this.ManagerComment.fbeSize), buffer, this.ManagerComment.fbeOffset + this.ManagerComment.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelManagerOrderCreateParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelManagerOrderCreateParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.ManagerComment.fbeSize + this.ExecutionPrice.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelManagerOrderCreateParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.ManagerComment.fbeExtra + this.ExecutionPrice.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelManagerOrderCreateParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelManagerOrderCreateParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelManagerOrderCreateParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 12044
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelManagerOrderCreateParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelManagerOrderCreateParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ManagerComment.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ManagerComment.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ManagerComment.fbeSize

    if ((fbeCurrentSize + this.ExecutionPrice.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ExecutionPrice.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ExecutionPrice.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelManagerOrderCreateParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelManagerOrderCreateParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelManagerOrderCreateParams}
   * @param {!ManagerOrderCreateParams} fbeValue Default value, defaults is new ManagerOrderCreateParams()
   * @returns {!ManagerOrderCreateParams} ManagerOrderCreateParams value
   */
  get (fbeValue = new ManagerOrderCreateParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelManagerOrderCreateParams}
   * @param {!ManagerOrderCreateParams} fbeValue ManagerOrderCreateParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.ManagerComment.fbeSize) <= fbeStructSize) {
      fbeValue.ManagerComment = this.ManagerComment.get()
    } else {
      fbeValue.ManagerComment = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ManagerComment.fbeSize

    if ((fbeCurrentSize + this.ExecutionPrice.fbeSize) <= fbeStructSize) {
      fbeValue.ExecutionPrice = this.ExecutionPrice.get()
    } else {
      fbeValue.ExecutionPrice = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ExecutionPrice.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelManagerOrderCreateParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelManagerOrderCreateParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelManagerOrderCreateParams}
   * @param {!ManagerOrderCreateParams} fbeValue ManagerOrderCreateParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelManagerOrderCreateParams}
   * @param {ManagerOrderCreateParams} fbeValue ManagerOrderCreateParams value
   */
  setFields (fbeValue) {
    this.ManagerComment.set(fbeValue.ManagerComment)
    this.ExecutionPrice.set(fbeValue.ExecutionPrice)
  }
}

export { FieldModelManagerOrderCreateParams };

/**
 * Fast Binary Encoding ManagerOrderCreateParams model
 */
class ManagerOrderCreateParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelManagerOrderCreateParams(this.buffer, 4)
  }

  /**
   * Get the ManagerOrderCreateParams model
   * @this {!ManagerOrderCreateParamsModel}
   * @returns {!FieldModelManagerOrderCreateParams} model ManagerOrderCreateParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ManagerOrderCreateParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ManagerOrderCreateParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ManagerOrderCreateParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ManagerOrderCreateParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelManagerOrderCreateParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ManagerOrderCreateParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ManagerOrderCreateParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ManagerOrderCreateParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ManagerOrderCreateParamsModel}
   * @param {!ManagerOrderCreateParams} value ManagerOrderCreateParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ManagerOrderCreateParamsModel}
   * @param {!ManagerOrderCreateParams} value ManagerOrderCreateParams value, defaults is new ManagerOrderCreateParams()
   * @return {!object} Deserialized ManagerOrderCreateParams value and its size
   */
  deserialize (value = new ManagerOrderCreateParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ManagerOrderCreateParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ManagerOrderCreateParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ManagerOrderCreateParamsModel}
   * @param {!number} prev Previous ManagerOrderCreateParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ManagerOrderCreateParamsModel };
